import {
    Box,
    Button,
    Card,
    CardMedia,
    CircularProgress,
    Dialog,
    DialogContent,
    Divider,
    Grid,
    Stack,
    styled,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { PDFViewer } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import SVG from '../../../assets/colorPicker.png';
import design1 from '../../../assets/design_01.png';
import design2 from '../../../assets/design_02.png';
import design3 from '../../../assets/design_03.png';
import InvoiceDesign1 from '../../../common/ReactPdfPreview/InvoiceDesign1';
import InvoiceDesign2 from '../../../common/ReactPdfPreview/InvoiceDesign2';
import InvoiceDesign3 from '../../../common/ReactPdfPreview/InvoiceDesign3';
import InvoiceDesign4 from '../../../common/ReactPdfPreview/InvoiceDesign4';
import { getPdfTemplateApi, updatePdfTemplateSetting } from '../../../redux/Account/PDFSetting/action';
import { PART_TYPES } from '../../../utility/Constant';
import SuccessMessageModal from '../../Common/SuccessMessage';

// Constants
const INVOICE_DESIGNS = [
    { id: 'design_01', label: 'DESIGN 01', preview: design1 },
    { id: 'design_02', label: 'DESIGN 02', preview: design2 },
    { id: 'design_03', label: 'DESIGN 03', preview: design3 },
    // Preview uses design_02 asset until design_04.png exists; PDF preview uses InvoiceDesign2 stand-in.
    { id: 'design_04', label: 'DESIGN 04', preview: design2 },
];

const DEFAULT_COLORS = {
    primary: ['#000000', '#FF0000', '#0C17E1', '#E7FF0E', '#FF0051', '#00EEFF', '#2DED1F'],
    secondary: ['#E0E0E0', '#FFCDCD', '#DBDDFF', '#FFECD5', '#FFD4E2', '#DFF8FF', '#D7FFD4'],
};

// Design-specific default colors
const DESIGN_COLORS = {
    design_01: { header: '#006CDD', text: '#006CDD' },
    design_02: { header: '#DBDBDB', text: '#DBDBDB' },
    design_03: { header: '#D9AC33', text: '#D9AC33' },
    design_04: { header: '#DBDBDB', text: '#DBDBDB' },
};

const PREVIEW_COMPONENT_BY_DESIGN = {
    design_01: InvoiceDesign1,
    design_02: InvoiceDesign2,
    design_03: InvoiceDesign3,
    design_04: InvoiceDesign4,
};

const ALL_CHECKBOX_ITEMS = [
    // { key: 'partName', value: 'Part Name' },
    { key: 'partNumber', value: 'Part #' },
    { key: 'partBrand', value: 'Brand' },
    // { key: 'partCategory', value: 'Category' },
    { key: 'tyreDimension', value: 'Tire Dimension' },
    { key: 'tyreModel', value: 'Tire Model' },
    { key: 'partProvider', value: 'Parts Provider' },
    { key: 'partDescription', value: 'Description' },
];

// Map allowed checkboxes per part type
const CHECKBOX_WHITELIST = {
    [PART_TYPES.PARTS]: ['partNumber', 'partBrand', 'partProvider', 'partDescription'],
    [PART_TYPES.TYRE]: ['partNumber', 'tyreModel', 'partBrand', 'partDescription'],
};

const INITIAL_INVOICE_SETTINGS = {
    type: 'invoice',
    designName: 'design_01',
    color1: '#000',
    color2: '#000',
    color3: '#000',
    color4: '#000',
    shopLogo: null,
    shopLogoSquare: null,
};

const InvoiceSettings = ({ settingVisibility, isUpdating, configData, shopDetailsData }) => {
    // Refs for color picker inputs
    const color1PickerRef = useRef(null);
    const color2PickerRef = useRef(null);
    const color3PickerRef = useRef(null);
    const color4PickerRef = useRef(null);

    // Redux and localStorage
    const dispatch = useDispatch();
    const shopId = useMemo(() => JSON.parse(localStorage.getItem('apex-saas_details'))?._id, []);
    const shopDetails = useMemo(() => JSON.parse(localStorage.getItem('apex-saas_details')), []);

    // State management
    const [invoiceSettings, setInvoiceSettings] = useState(INITIAL_INVOICE_SETTINGS);
    const [togglePartType, setTogglePartType] = useState(PART_TYPES.PARTS);
    const [headerColors, setHeaderColors] = useState([...DEFAULT_COLORS.primary]);
    const [textColors, setTextColors] = useState([...DEFAULT_COLORS.primary]);
    const [storeData, setStoreData] = useState([]);
    const [circularLogo, setCircularLogo] = useState(null);
    const [squareLogo, setSquareLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showLoader, setShowLoader] = useState(false);
    const [openSuccessModal, setOpenSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [key, setKey] = useState(0);

    // NEW: State to track uploaded logos separately from API data
    const [uploadedLogos, setUploadedLogos] = useState({
        circular: null,
        square: null,
    });

    const { handleSubmit } = useForm({});

    // Shop country drives the US-only Tax Code column in the tax summary table
    const shopCountryCode = useMemo(() => {
        const countryName = shopDetailsData?.country?.country || shopDetailsData?.country || shopDetails?.country?.country || shopDetails?.country || '';
        return String(countryName).trim().toLowerCase() === 'united states' ? 'US' : 'CA';
    }, [shopDetailsData, shopDetails]);

    // Memoized shop template settings for PDF preview
    const templateSettings = useMemo(
        () => ({
            ...invoiceSettings,
            shopName: shopDetails?.shopName,
            shopAddress: shopDetails?.address,
            shopPhone: shopDetails?.shopPhone || shopDetailsData?.shopPhone || '(416)-123-2780',
            shopEmail: shopDetails?.shopEmail || shopDetailsData?.shopEmail || 'autorepair@apex.com',
            shopWebsite: shopDetails?.shopWebsite || shopDetailsData?.shopWebsite || 'www.autorepair.com',
            shopTagline: shopDetails?.shopTagline,
            countryCode: shopCountryCode,
            dates: {
                invoiceDate: dayjs().format('YYYY-MM-DD'),
                droppedOff: dayjs().format('YYYY-MM-DD'),
                workCompletion: dayjs().format('YYYY-MM-DD'),
            },
            display: settingVisibility,
        }),
        [invoiceSettings, shopDetails, shopDetailsData, settingVisibility, shopCountryCode]
    );
    console.log('🚀 ~ InvoiceSettings ~ templateSettings:', templateSettings);

    // Initialize data on component mount
    useEffect(() => {
        fetchApiData();
    }, []);

    useEffect(() => {
        if (!isUpdating) {
            console.log('🚀 ~ InvoiceSettings ~ isUpdating:', isUpdating);
            setTimeout(() => {
                triggerPreviewRerender();
            }, 500);
        }
    }, [isUpdating]);

    // Handle design change effects
    useEffect(() => {
        updateColorsForDesign();
        updateSettingsFromStoreData();
        triggerPreviewRerender();
    }, [invoiceSettings.designName]);

    // Handle preview rerender when settings change
    useEffect(() => {
        triggerPreviewRerender();
    }, [invoiceSettings]);

    // Cleanup object URLs on unmount
    useEffect(() => {
        return () => {
            if (uploadedLogos.circular) URL.revokeObjectURL(uploadedLogos.circular);
            if (uploadedLogos.square) URL.revokeObjectURL(uploadedLogos.square);
        };
    }, [uploadedLogos]);

    /**
     * Fetch PDF template data from API
     */
    const fetchApiData = async () => {
        try {
            const response = await dispatch(getPdfTemplateApi());
            if (response?.length > 0) {
                setStoreData(response);
                const activeItem = response.find((item) => item.current);
                if (activeItem) {
                    setInvoiceSettings({
                        type: activeItem.type || 'invoice',
                        designName: activeItem.designName || 'design_01',
                        color1: activeItem.color1,
                        color2: activeItem.color2,
                        color3: activeItem.color3,
                        color4: activeItem.color4,
                        shopLogo: activeItem.shop?.shopLogo || null,
                        shopLogoSquare: activeItem.shop?.shopSquareLogo || null,
                        display: activeItem.display || INITIAL_INVOICE_SETTINGS.display,
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching PDF template data:', error);
        }
    };

    /**
     * Update color palettes based on selected design
     */
    const updateColorsForDesign = useCallback(() => {
        const designColors = DESIGN_COLORS[invoiceSettings.designName];
        if (designColors) {
            const newHeaderColors = [...DEFAULT_COLORS.primary];
            const newTextColors = [...DEFAULT_COLORS.primary];

            newHeaderColors[0] = designColors.header;
            newTextColors[0] = designColors.text;

            setHeaderColors(newHeaderColors);
            setTextColors(newTextColors);
        }
    }, [invoiceSettings.designName]);

    /**
     * Update settings from store data when design changes - FIXED VERSION
     */
    const updateSettingsFromStoreData = useCallback(() => {
        const currentStoreItem =
            storeData.find((item) => item.designName === invoiceSettings.designName) ||
            (invoiceSettings.designName === 'design_04' ? storeData.find((item) => item.designName === 'design_02') : undefined);
        if (currentStoreItem) {
            setInvoiceSettings((prev) => ({
                ...prev,
                type: currentStoreItem.type,
                color1: currentStoreItem.color1,
                color2: currentStoreItem.color2,
                color3: currentStoreItem.color3,
                color4: currentStoreItem.color4,
                // FIXED: Preserve uploaded logos, fallback to API data only if no uploaded logos
                shopLogo: uploadedLogos.circular || currentStoreItem.shop?.shopLogo || prev.shopLogo,
                shopLogoSquare: uploadedLogos.square || currentStoreItem.shop?.shopSquareLogo || prev.shopLogoSquare,
                display: currentStoreItem.display,
            }));
        }
    }, [storeData, invoiceSettings.designName, uploadedLogos]);

    /**
     * Force PDF preview re-render with loading state
     */
    const triggerPreviewRerender = useCallback(() => {
        setIsLoading(true);
        setKey((prevKey) => prevKey + 1);

        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 300);

        return () => clearTimeout(timer);
    }, []);

    // Replace the generic triggerColorPicker function with specific handlers
    const triggerColor1Picker = useCallback(() => {
        if (color1PickerRef.current) {
            color1PickerRef.current.click();
        }
    }, []);

    const triggerColor2Picker = useCallback(() => {
        if (color2PickerRef.current) {
            color2PickerRef.current.click();
        }
    }, []);

    const triggerColor3Picker = useCallback(() => {
        if (color3PickerRef.current) {
            color3PickerRef.current.click();
        }
    }, []);

    const triggerColor4Picker = useCallback(() => {
        if (color4PickerRef.current) {
            color4PickerRef.current.click();
        }
    }, []);

    /**
     * Handle logo file upload - FIXED VERSION
     */
    const handleLogoChange = useCallback(
        (type, event) => {
            const file = event.target.files?.[0];
            if (!file) return;

            const previewUrl = URL.createObjectURL(file);

            if (type === 'circular') {
                // Clean up previous URL
                if (uploadedLogos.circular) {
                    URL.revokeObjectURL(uploadedLogos.circular);
                }

                setCircularLogo(file);
                setUploadedLogos((prev) => ({ ...prev, circular: previewUrl }));
                setInvoiceSettings((prev) => ({ ...prev, shopLogo: previewUrl }));
            } else {
                // Clean up previous URL
                if (uploadedLogos.square) {
                    URL.revokeObjectURL(uploadedLogos.square);
                }

                setSquareLogo(file);
                setUploadedLogos((prev) => ({ ...prev, square: previewUrl }));
                setInvoiceSettings((prev) => ({ ...prev, shopLogoSquare: previewUrl }));
            }
        },
        [uploadedLogos]
    );

    /**
     * Handle checkbox state changes
     */
    const handleChangeCheckBox = useCallback((key) => {
        setInvoiceSettings((prev) => ({
            ...prev,
            display: {
                ...prev.display,
                service: {
                    ...prev.display.service,
                    [key]: !prev.display.service[key],
                },
            },
        }));
    }, []);

    /**
     * Handle color selection
     */
    const handleColorChange = useCallback((colorType, color) => {
        setInvoiceSettings((prev) => ({ ...prev, [colorType]: color }));
    }, []);

    /**
     * Handle design selection
     */
    const handleDesignChange = useCallback((designId) => {
        setInvoiceSettings((prev) => ({ ...prev, designName: designId }));
    }, []);

    /**
     * Build FormData for API submission
     */
    const buildFormData = useCallback(() => {
        const formData = new FormData();
        const settingsClone = { ...invoiceSettings };

        // Remove logo URLs from clone (handled separately)
        delete settingsClone.shopLogo;
        delete settingsClone.shopLogoSquare;

        // Append logo files if available
        if (circularLogo) formData.append('shopLogo', circularLogo);
        if (squareLogo) formData.append('shopSquareLogo', squareLogo);

        // Append settings data
        Object.entries(settingsClone).forEach(([key, value]) => {
            if (key === 'display') {
                // Handle nested display object
                Object.entries(value).forEach(([subKey, subValue]) => {
                    if (typeof subValue === 'object') {
                        // Object.entries(subValue).forEach(([innerKey, innerValue]) => {
                        //     formData.append(`display[${subKey}][${innerKey}]`, innerValue);
                        // });
                    } else {
                        formData.append(`display[${subKey}]`, subValue);
                    }
                });
            } else {
                formData.append(key, value);
            }
        });

        return formData;
    }, [invoiceSettings, circularLogo, squareLogo]);

    /**
     * Submit form data to API - UPDATED VERSION
     */
    const onSubmit = async () => {
        setShowLoader(true);

        try {
            const formData = buildFormData();
            const response = await dispatch(updatePdfTemplateSetting(formData));

            if (response) {
                await fetchApiData();
                // Clear uploaded logos state after successful save since they're now in API
                setUploadedLogos({ circular: null, square: null });
                setCircularLogo(null);
                setSquareLogo(null);
                setSuccessMessage('Settings updated successfully');
                setOpenSuccessModal(true);
            }
        } catch (error) {
            console.error('Error updating settings:', error);
        } finally {
            setShowLoader(false);
        }
    };

    const handelChangesCancel = async () => {
        try {
            const response = await dispatch(getPdfTemplateApi());
            if (response?.length > 0) {
                setStoreData(response);
                const activeItem = response.find((item) => item.current);
                if (activeItem) {
                    setInvoiceSettings({
                        type: activeItem.type || 'invoice',
                        designName: activeItem.designName || 'design_01',
                        color1: activeItem.color1,
                        color2: activeItem.color2,
                        color3: activeItem.color3,
                        color4: activeItem.color4,
                        shopLogo: activeItem.shop?.shopLogo || null,
                        shopLogoSquare: activeItem.shop?.shopSquareLogo || null,
                        display: activeItem.display || INITIAL_INVOICE_SETTINGS.display,
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching PDF template data:', error);
        }
    };

    /**
     * Render color selection UI
     */
    const renderColorSelection = useCallback(
        (colors, selectedColor, onColorSelect, customColorRef, onCustomClick, label) => (
            <Box>
                <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5 }}>
                    {label}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    {colors.map((color) => (
                        <Box
                            key={color}
                            onClick={() => onColorSelect(color)}
                            sx={{
                                mx: 1,
                                width: 30,
                                height: 30,
                                borderRadius: '50%',
                                padding: 0,
                                margin: 0,
                                backgroundColor: color,
                                cursor: 'pointer',
                                position: 'relative',
                                border: selectedColor === color ? `1.5px solid ${color}` : 'none',
                                '&::after':
                                    selectedColor === color
                                        ? {
                                              content: '""',
                                              position: 'absolute',
                                              top: -4,
                                              left: -4,
                                              right: -4,
                                              bottom: -4,
                                              borderRadius: '50%',
                                              border: `1.5px solid ${color}`,
                                          }
                                        : {},
                            }}
                        />
                    ))}

                    {/* Custom color picker with specific ref */}
                    <div style={{ position: 'relative' }}>
                        <input
                            type="color"
                            ref={customColorRef}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                left: '0',
                                width: '30px',
                                height: '30px',
                                zIndex: 1,
                                opacity: 0,
                                pointerEvents: 'none',
                            }}
                            onChange={(event) => onColorSelect(event.target.value)}
                            value={selectedColor}
                        />
                    </div>

                    <Box
                        onClick={onCustomClick}
                        sx={{
                            marginLeft: '0px!important',
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            position: 'relative',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: !colors.includes(selectedColor) ? `2px solid ${selectedColor}` : 'none',
                        }}
                    >
                        <img src={SVG} alt="Custom color picker" style={{ width: '30px', height: '30px' }} />
                    </Box>
                </Stack>
            </Box>
        ),
        []
    );

    /**
     * Render logo upload section
     */
    const renderLogoUpload = useCallback(
        (type, logo, inputId, buttonText) => (
            <Box display="flex" alignItems="center" gap={1.5}>
                {logo ? (
                    <img
                        src={logo}
                        alt={`${type} Logo Preview`}
                        style={{
                            height: '63px',
                            width: '63px',
                            objectFit: 'contain',
                            borderRadius: type === 'circular' ? '50%' : '0',
                            border: '1px solid #F1F1F1',
                        }}
                    />
                ) : (
                    <div
                        style={{
                            background: '#F1F1F1',
                            height: '63px',
                            width: '63px',
                            borderRadius: type === 'circular' ? '50%' : '0',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31" viewBox="0 0 31 31" fill="none">
                            <path
                                d="M6.45834 27.1251C5.74792 27.1251 5.13998 26.8723 4.6345 26.3669C4.12903 25.8614 3.87586 25.253 3.875 24.5417V6.45834C3.875 5.74792 4.12817 5.13998 4.6345 4.6345C5.14084 4.12903 5.74878 3.87586 6.45834 3.875H24.5417C25.2521 3.875 25.8605 4.12817 26.3669 4.6345C26.8732 5.14084 27.1259 5.74878 27.1251 6.45834V24.5417C27.1251 25.2521 26.8723 25.8605 26.3669 26.3669C25.8614 26.8732 25.253 27.1259 24.5417 27.1251H6.45834ZM6.45834 24.5417H24.5417V6.45834H6.45834V24.5417ZM7.75001 21.9584H23.2501L18.4063 15.5L14.5313 20.6667L11.625 16.7917L7.75001 21.9584Z"
                                fill="black"
                            />
                        </svg>
                    </div>
                )}

                <input accept="image/*" id={inputId} type="file" hidden onChange={(e) => handleLogoChange(type, e)} />
                <Button
                    variant="outlined"
                    component="label"
                    htmlFor={inputId}
                    startIcon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                            <path
                                d="M11 16.5V8.35L8.4 10.95L7 9.5L12 4.5L17 9.5L15.6 10.95L13 8.35V16.5H11ZM6 20.5C5.45 20.5 4.97933 20.3043 4.588 19.913C4.19667 19.5217 4.00067 19.0507 4 18.5V15.5H6V18.5H18V15.5H20V18.5C20 19.05 19.8043 19.521 19.413 19.913C19.0217 20.305 18.5507 20.5007 18 20.5H6Z"
                                fill="black"
                                fillOpacity="0.6"
                            />
                        </svg>
                    }
                    sx={{
                        padding: '4px 8px',
                        background: '#F1F1F1',
                        height: '32px',
                        textTransform: 'none',
                        borderColor: '#F1F1F1',
                        color: 'rgba(0, 0, 0, 0.60);',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: '0.15px',
                        '&:hover': { borderColor: '#F1F1F1' },
                    }}
                >
                    {buttonText}
                </Button>
            </Box>
        ),
        [handleLogoChange]
    );

    /**
     * Render PDF preview based on selected design
     */
    const renderPDFPreview = useMemo(() => {
        const DesignComponent = PREVIEW_COMPONENT_BY_DESIGN[invoiceSettings.designName];

        return DesignComponent ? <DesignComponent templateSettings={templateSettings} configData={configData} /> : null;
    }, [invoiceSettings.designName, templateSettings, configData]);

    const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
        borderRadius: '30px',
        padding: '6px',
        background: '#E9E9EA',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        width: 'max-content',
        '.Mui-disabled': {
            border: 'none!important',
            borderColor: 'none!important',
        },
        '& .MuiToggleButton-root': {
            border: 'none',
            color: '#FFF',
            fontFamily: 'Roboto',
            fontSize: '16px',
            fontWeight: 400,
            padding: '0 16px',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            borderRadius: '50px',
            transition: 'none', // Disable hover effects
            textTransform: 'capitalize',
            height: '40px',

            '&.Mui-selected': {
                backgroundColor: '#006CDD',
                // border: "1px solid #0066E1",
                color: '#FFF',
                // boxShadow: "0px 2px 2px 0px rgba(0, 122, 255, 0.25)",
            },

            '&:not(.Mui-selected)': {
                backgroundColor: '#E9E9EA',
                // border: "1px solid #E9E9EA",
                color: '#000',
            },

            '&:not(.Mui-selected):hover': {
                backgroundColor: '#E9E9EA',
            },

            '&.Mui-selected:hover': {
                backgroundColor: '#007AFF',
            },
        },
    }));

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid
                    container
                    sx={{
                        boxShadow: 3,
                        bgcolor: 'white',
                        padding: '24px',
                        borderTop: 'none',
                        borderRadius: '10px',
                        marginTop: '32px',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        rowGap: { xs: 3, sm: 3, md: 3, lg: 0 },
                        columnGap: { lg: '24px' },
                    }}
                >
                    {/* Left Panel - Settings */}
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={4}
                        // sx={{
                        //     width: { lg: 'auto' },
                        //     maxWidth: '100%',
                        //     flexGrow: { lg: 0 },
                        //     flexShrink: { lg: 0 },
                        //     flexBasis: { lg: 'auto' },
                        // }}
                    >
                        <Stack gap={0}>
                            <Stack gap={0.5}>
                                <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                                    Document Settings
                                </Typography>
                                <Typography
                                    sx={{
                                        color: '#000',
                                        fontSize: '15px',
                                        fontWeight: 400,
                                        lineHeight: '120%',
                                        letterSpacing: '0.15px',
                                    }}
                                >
                                    Applies to invoices, estimates, and work orders.
                                </Typography>
                            </Stack>

                            <Divider sx={{ borderColor: '#E0E0E0', mt: '12px', mb: '20px' }} />

                            {/* Logo Upload Section */}
                            <Stack gap={1.5}>
                                <Typography variant="subtitle1" fontWeight="medium">
                                    Select Logo
                                </Typography>
                                {renderLogoUpload('circular', invoiceSettings.shopLogo, 'circular-logo-upload', 'Choose Circular Logo')}
                                {renderLogoUpload('square', invoiceSettings.shopLogoSquare, 'square-logo-upload', 'Choose Square Logo')}
                            </Stack>

                            <Divider sx={{ borderColor: '#E0E0E0', mt: '20px', mb: '20px' }} />

                            {/* Color Selection Sections */}
                            <Stack gap={3}>
                                {renderColorSelection(
                                    headerColors,
                                    invoiceSettings.color1,
                                    (color) => handleColorChange('color1', color),
                                    color1PickerRef,
                                    triggerColor1Picker,
                                    'Table Header Color'
                                )}

                                {renderColorSelection(
                                    textColors,
                                    invoiceSettings.color2,
                                    (color) => handleColorChange('color2', color),
                                    color2PickerRef,
                                    triggerColor2Picker,
                                    'Table Header Text Color'
                                )}

                                {/* Design 3 specific color */}
                                {invoiceSettings.designName === 'design_03' &&
                                    renderColorSelection(
                                        DEFAULT_COLORS.primary,
                                        invoiceSettings.color4,
                                        (color) => handleColorChange('color4', color),
                                        color4PickerRef,
                                        triggerColor4Picker,
                                        'Table Header Background Color'
                                    )}

                                {/* Shop name header colour for design 3 */}
                                {invoiceSettings.designName === 'design_03' &&
                                    renderColorSelection(
                                        headerColors,
                                        invoiceSettings.color3,
                                        (color) => handleColorChange('color3', color),
                                        color3PickerRef,
                                        triggerColor3Picker,
                                        'Shop Name Color'
                                    )}

                                {/* Border Color for non-design_03 */}
                                {invoiceSettings.designName !== 'design_03' &&
                                    renderColorSelection(
                                        DEFAULT_COLORS.secondary,
                                        invoiceSettings.color3,
                                        (color) => handleColorChange('color3', color),
                                        color3PickerRef,
                                        triggerColor3Picker,
                                        'Border Color'
                                    )}
                            </Stack>

                            <Divider sx={{ borderColor: '#E0E0E0', mt: '20px', mb: '20px' }} />
                        </Stack>
                    </Grid>

                    {/* Middle Panel - Design Selection (237px at lg+) */}
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={6}
                        lg={2}
                        sx={{
                            width: { xs: '100%', sm: '100%', md: '100%', lg: '100%' },
                            // maxWidth: { xs: '237px', sm: '237px', md: '237px', lg: '237px' },
                            // flexBasis: { lg: '237px' },
                            // flexGrow: { lg: 0 },
                            // flexShrink: { lg: 0 },
                            mx: { xs: 'auto', sm: 'auto', md: 0, lg: 0 },
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: '237px', mx: { xs: 'auto', md: 0 } }}>
                            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5, fontSize: '16px' }}>
                                Select Document Design
                            </Typography>

                            <Grid container spacing={2}>
                                {INVOICE_DESIGNS.map((design) => {
                                    const isSelected = invoiceSettings.designName === design.id;
                                    return (
                                        <Grid item xs={12} key={design.id} sx={{}}>
                                            <Card
                                                sx={{
                                                    border: isSelected ? '2px solid #007AFF' : '1px solid rgba(0, 0, 0, 0.12)',
                                                    cursor: 'pointer',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    borderRadius: 1,
                                                    overflow: 'visible',
                                                    position: 'relative',
                                                    pb: '30px',
                                                    boxShadow: 'none',
                                                }}
                                                onClick={() => handleDesignChange(design.id)}
                                            >
                                                <CardMedia
                                                    component="img"
                                                    height="100%"
                                                    image={design.preview}
                                                    alt={design.label}
                                                    sx={{ p: 0, objectFit: 'fill!important' }}
                                                />
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '30px',
                                                        padding: '5.5px 8px',
                                                        backgroundColor: isSelected ? '#006CDD' : 'rgba(241, 241, 241, 0.87)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-start',
                                                        borderBottomLeftRadius: 1,
                                                        borderBottomRightRadius: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle1"
                                                        fontWeight="500"
                                                        letterSpacing="0.46px"
                                                        fontSize={16}
                                                        sx={{
                                                            color: isSelected ? 'white' : '#333333',
                                                            textTransform: 'uppercase',
                                                        }}
                                                    >
                                                        {design.label}
                                                    </Typography>
                                                </Box>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>
                    </Grid>

                    {/* Right Panel - PDF Preview (fills remaining width at lg+) */}
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={5.5}
                        sx={{
                            // flexGrow: { lg: 1 },
                            // flexBasis: { lg: 0 },
                            // minWidth: { lg: 0 },
                            // width: { xs: '100%', lg: 'auto' },
                            // maxWidth: '100%',
                            // display: 'flex',
                            justifyContent: { xs: 'center', md: 'center', lg: 'stretch' },
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                // height: { xs: 'auto', lg: 'auto' },
                                // minHeight: { xs: 280, lg: 684 },
                                // maxHeight: { lg: 'auto' },
                                // aspectRatio: { xs: '483 / 684', lg: 'auto' },
                                boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
                                overflow: 'hidden',
                            }}
                        >
                            {isLoading || isUpdating ? (
                                <Stack sx={{ width: '100%', height: '100%', minHeight: { xs: 280, lg: 684 } }} justifyContent="center" alignItems="center">
                                    <CircularProgress />
                                </Stack>
                            ) : (
                                <PDFViewer
                                    key={key}
                                    showToolbar={false}
                                    width="100%"
                                    height="684"
                                    style={{
                                        border: 'none',
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '684px',
                                        outline: 'none',
                                        visibility: isLoading ? 'hidden' : 'visible',
                                    }}
                                >
                                    {renderPDFPreview}
                                </PDFViewer>
                            )}
                        </Box>
                    </Grid>

                    {/* Action Buttons */}
                    <Grid container item xs={12} justifyContent="flex-end">
                        <Box display="flex" justifyContent="flex-end" gap={2} sx={{ mt: 4 }}>
                            <Button v className="btn-text-size-manage" variant="outlined" color="primary" onClick={handelChangesCancel}>
                                CANCEL
                            </Button>
                            <Button className="btn-text-size-manage" variant="contained" color="primary" onClick={onSubmit}>
                                SAVE
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>

            {/* Loading Dialog */}
            <Dialog fullScreen={false} open={showLoader} aria-labelledby="loading-dialog">
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            {/* Success Modal */}
            <SuccessMessageModal message={successMessage} open={openSuccessModal} setOpenSuccessModal={setOpenSuccessModal} />
        </Box>
    );
};

export default InvoiceSettings;
